import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import apiService from '../services/apiService';
import { 
  Users, 
  Home,
  ChevronRight,
  UserPlus,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Heart,
  Search,
  Filter,
  Eye,
  Edit,
  Trash2,
  Plus,
  X,
  Save,
  RefreshCw,
  ChevronLeft,
  ChevronRight as ChevronRightIcon,
  AlertTriangle,
  Download,
  Upload,
  FileSpreadsheet
} from 'lucide-react';

const MembersPage = () => {
  const { user, isAuthenticated, isReadOnly } = useAuth();
  const navigate = useNavigate();
  
  // États de base
  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // États de recherche et filtrage
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterZone, setFilterZone] = useState('');
  const [filterFraternity, setFilterFraternity] = useState('');
  
  // État pour la pagination
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0,
    from: 1,
    to: 0
  });
  
  // États pour modals et CRUD
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [editingMember, setEditingMember] = useState(null);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedMemberDetails, setSelectedMemberDetails] = useState(null);
  const [coupleContributions, setCoupleContributions] = useState([]);
  
  // États pour l'import/export
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  
  // États pour les cotisations
  const [showAddContributionModal, setShowAddContributionModal] = useState(false);
  const [selectedMemberForContribution, setSelectedMemberForContribution] = useState(null);
  const [contributionFormData, setContributionFormData] = useState({
    amount: '',
    period_month: new Date().getMonth() + 1,
    period_year: new Date().getFullYear(),
    status: 'pending',
    paid_at: '',
    notes: ''
  });
  const [contributionLoading, setContributionLoading] = useState(false);
  const [contributionError, setContributionError] = useState('');
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    role: 'membre',
    status: 'actif',
    is_admin: false,
    couple_name: '',
    marriage_date: '',
    zone: '',
    fraternity: ''
  });

  // États des statistiques
  const [stats, setStats] = useState({
    total_members: 0,
    active_members: 0,
    new_this_month: 0,
    members_by_role: {},
    growth_rate: 0
  });
  const [statsLoading, setStatsLoading] = useState(true);

  // États pour les options d'organisation
  const [organizationOptions, setOrganizationOptions] = useState({
    zones: [],
    fraternities: []
  });

  // Redirection si non connecté
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
  }, [isAuthenticated, navigate]);

  // Charger les statistiques
  const loadStats = useCallback(async () => {
    console.log('📊 Chargement des statistiques...');
    setStatsLoading(true);
    try {
      const response = await apiService.get('/members/stats');
      console.log('📊 Réponse stats:', response);
      if (response.data && response.data.data) {
        console.log('📊 Données stats:', response.data.data);
        setStats(response.data.data);
      } else if (response.data) {
        console.log('📊 Structure alternative:', response.data);
        setStats(response.data);
      }
    } catch (error) {
      console.error('❌ Erreur lors du chargement des statistiques:', error);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  // Charger les membres avec filtres et pagination
  const loadMembers = useCallback(async (page = pagination.current_page) => {
    setIsLoading(true);
    try {
      const params = {
        page,
        per_page: pagination.per_page
      };
      
      if (searchTerm) params.search = searchTerm;
      if (filterStatus) params.status = filterStatus;
      if (filterRole) params.role = filterRole;
      if (filterZone) params.zone = filterZone;
      if (filterFraternity) params.fraternity = filterFraternity;
      
      console.log('🔍 Filtres envoyés à l\'API:', params);
      
      const response = await apiService.get('/members', { params });
      console.log('👥 Réponse membres:', response);
      
      if (response.data) {
        const membersData = response.data.data || response.data;
        console.log('👥 Données membres:', membersData);
        console.log('👥 Nombre de membres reçus:', Array.isArray(membersData) ? membersData.length : 0);
        if (Array.isArray(membersData) && membersData.length > 0) {
          console.log('👥 Premier membre exemple:', membersData[0]);
          console.log('👥 Fraternités des membres:', membersData.map(m => m.fraternity).filter((v, i, a) => a.indexOf(v) === i));
        }
        console.log('👥 Pagination brute:', {
          current_page: response.data.current_page,
          last_page: response.data.last_page,
          total: response.data.total,
          per_page: response.data.per_page,
          from: response.data.from,
          to: response.data.to
        });
        
        // S'assurer que membersData est un tableau
        const finalMembers = Array.isArray(membersData) ? membersData : [];
        setMembers(finalMembers);
        
        // Utiliser les données de pagination du backend ou calculer intelligemment
        const backendTotal = response.data.total;
        const backendLastPage = response.data.last_page;
        const backendCurrentPage = response.data.current_page;
        const backendPerPage = response.data.per_page;
        const backendFrom = response.data.from;
        const backendTo = response.data.to;
        
        console.log('🔍 Données backend détaillées:', {
          total: backendTotal,
          last_page: backendLastPage,
          current_page: backendCurrentPage,
          per_page: backendPerPage,
          from: backendFrom,
          to: backendTo,
          membres_récupérés: finalMembers.length
        });
        
        // Si le backend renvoie les bonnes données de pagination, les utiliser
        let paginationData;
        if (backendTotal && backendLastPage) {
          // Le backend fournit une pagination complète
          paginationData = {
            current_page: backendCurrentPage || 1,
            last_page: backendLastPage,
            total: backendTotal,
            per_page: backendPerPage || 15,
            from: backendFrom || 1,
            to: backendTo || finalMembers.length
          };
        } else {
          // Fallback : estimation basée sur les données disponibles
          const estimatedTotal = finalMembers.length < 15 ? finalMembers.length : 36; // Estimation
          const estimatedLastPage = Math.ceil(estimatedTotal / 15);
          
          paginationData = {
            current_page: page || 1,
            last_page: estimatedLastPage,
            total: estimatedTotal,
            per_page: 15,
            from: ((page || 1) - 1) * 15 + 1,
            to: Math.min((page || 1) * 15, estimatedTotal)
          };
        }
        
        console.log('👥 Pagination finale utilisée:', paginationData);
        console.log('👥 Navigation possible:', {
          canGoBack: paginationData.current_page > 1,
          canGoForward: paginationData.current_page < paginationData.last_page,
          showNavigation: paginationData.last_page > 1
        });
        
        setPagination(paginationData);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des membres:', error);
      setMembers([]);
    } finally {
      setIsLoading(false);
    }
  }, [searchTerm, filterStatus, filterRole, filterZone, filterFraternity, pagination.per_page]);

  // Charger les options d'organisation
  const loadOrganizationOptions = useCallback(async () => {
    try {
      console.log('🏢 Chargement des options d\'organisation...');
      const response = await apiService.get('/members/organization-options');
      console.log('🏢 Réponse complète:', response);
      console.log('🏢 response.data:', response.data);
      console.log('🏢 response.data.data:', response.data?.data);
      
      // Gérer les deux formats possibles de réponse
      let optionsData = null;
      if (response.data?.data) {
        optionsData = response.data.data;
      } else if (response.data?.zones && response.data?.fraternities) {
        optionsData = response.data;
      } else if (response.zones && response.fraternities) {
        optionsData = response;
      }
      
      if (optionsData) {
        console.log('🏢 Zones trouvées:', optionsData.zones);
        console.log('🏢 Fraternités trouvées:', optionsData.fraternities);
        setOrganizationOptions({
          zones: optionsData.zones || [],
          fraternities: optionsData.fraternities || []
        });
      } else {
        console.error('❌ Structure de réponse non reconnue');
      }
    } catch (error) {
      console.error('❌ Erreur lors du chargement des options d\'organisation:', error);
    }
  }, []);

  // Rafraîchir les données
  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([loadMembers(pagination.current_page), loadStats()]);
    setTimeout(() => setRefreshing(false), 1000);
  };

  // Charger données initiales
  useEffect(() => {
    console.log('🚀 Initialisation de MembersPage...');
    loadStats();
    loadMembers();
    loadOrganizationOptions();
  }, [loadStats, loadMembers, loadOrganizationOptions]);
  
  // Observer les changements dans organizationOptions
  useEffect(() => {
    console.log('📋 Options d\'organisation mises à jour:', organizationOptions);
  }, [organizationOptions]);

  // Recharger quand les filtres changent
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPagination(prev => ({ ...prev, current_page: 1 }));
      loadMembers(1);
    }, 500); // Debounce de 500ms

    return () => clearTimeout(timeoutId);
  }, [searchTerm, filterStatus, filterRole, filterZone, filterFraternity, loadMembers]);

  // Filtrage des membres - Pas de double filtrage car l'API fait déjà le travail
  // On utilise directement les membres renvoyés par l'API qui sont déjà filtrés
  const filteredMembers = useMemo(() => {
    if (!Array.isArray(members)) return [];
    
    // L'API fait déjà le filtrage par status, role, zone, fraternity
    // On retourne simplement les membres tels quels
    return members;
  }, [members]);

  // Gestion du formulaire
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Fonction helper pour formater les noms de champs
  const formatFieldName = (fieldName) => {
    const fieldMap = {
      'first_name': 'Prénom',
      'last_name': 'Nom',
      'email': 'Email',
      'password': 'Mot de passe',
      'phone': 'Téléphone',
      'date_of_birth': 'Date de naissance',
      'gender': 'Genre',
      'address': 'Adresse',
      'profession': 'Profession',
      'join_date': 'Date d\'adhésion',
      'role': 'Rôle',
      'status': 'Statut',
      'contribution_monthly': 'Contribution mensuelle',
      'notes': 'Notes'
    };
    return fieldMap[fieldName] || fieldName.charAt(0).toUpperCase() + fieldName.slice(1).replace('_', ' ');
  };

  // Réinitialiser le formulaire
  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      phone: '',
      date_of_birth: '',
      gender: '',
      address: '',
      profession: '',
      join_date: new Date().toISOString().split('T')[0], // Date du jour par défaut
      role: 'membre',
      status: 'actif',
      contribution_monthly: 500.00,
      notes: '',
      couple_name: '',
      marriage_date: '',
      zone: '',
      fraternity: ''
    });
    setFormErrors({});
    setFormError('');
  };

  // Ouvrir modal d'ajout
  const handleAddMember = () => {
    resetForm();
    setShowAddModal(true);
  };

  // Ouvrir modal d'édition
  const handleEditMember = (member) => {
    setSelectedMember(member);
    setFormData({
      first_name: member.first_name || '',
      last_name: member.last_name || '',
      email: member.email || '',
      phone: member.phone || '',
      role: member.role || 'membre',
      status: member.status || 'actif',
      address: member.address || '',
      date_of_birth: member.date_of_birth || '',
      profession: member.profession || '',
      notes: member.notes || '',
      couple_name: member.couple_name || '',
      marriage_date: member.marriage_date || '',
      zone: member.zone || '',
      fraternity: member.fraternity || ''
    });
    setShowCreateModal(true);
  };

  // Ouvrir modal de suppression
  const handleDeleteMember = (member) => {
    setSelectedMember(member);
    setShowDeleteModal(true);
  };

  // Voir les détails d'un membre
  const handleViewMemberDetails = async (member) => {
    setSelectedMemberDetails(member);
    setShowDetailsModal(true);
    
    // Charger les contributions du couple
    try {
      const response = await apiService.get(`/contributions/couple/${member.id}`);
      // L'API retourne { couple_data, contributions, statistics }
      setCoupleContributions(response.data.contributions || []);
    } catch (error) {
      console.error('Erreur lors du chargement des contributions du couple:', error);
      setCoupleContributions([]);
    }
  };

  // Créer un nouveau membre
  const handleCreateMember = async (e) => {
    e.preventDefault();
    try {
      await apiService.post('/members', formData);
      setShowAddModal(false);
      resetForm();
      loadMembers(pagination.current_page);
      loadStats();
    } catch (error) {
      console.error('Erreur lors de la création:', error);
      alert('Erreur lors de la création du membre');
    }
  };

  // Mettre à jour un membre
  // Supprimer un membre
  const handleDelete = async (memberId) => {
    setLoading(true);
    try {
      await apiService.delete(`/members/${memberId}`);
      setShowDeleteModal(false);
      setMemberToDelete(null);
      loadMembers(pagination.current_page);
      loadStats();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert('Erreur lors de la suppression du membre');
    } finally {
      setLoading(false);
    }
  };

  // Gestion des vues
  const handleView = (member) => {
    setSelectedMember(member);
    // Ici on pourrait ouvrir un modal de détails ou naviguer vers une page de détails
    console.log('Voir détails de:', member);
  };

  const handleEdit = (member) => {
    setEditingMember(member);
    setFormData({
      first_name: member.first_name || '',
      last_name: member.last_name || '',
      email: member.email || '',
      password: '', // Ne pas pré-remplir le mot de passe
      phone: member.phone || '',
      date_of_birth: member.date_of_birth || '',
      gender: member.gender || '',
      address: member.address || '',
      profession: member.profession || '',
      join_date: member.join_date || new Date().toISOString().split('T')[0],
      role: member.role || 'membre',
      status: member.status || 'actif',
      contribution_monthly: member.contribution_monthly || 500.00,
      notes: member.notes || '',
      zone: member.zone || '',
      fraternity: member.fraternity || '',
      couple_name: member.couple_name || '',
      marriage_date: member.marriage_date || ''
    });
    setShowCreateModal(true);
  };

  // Gestion du formulaire unifié
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Réinitialiser les erreurs
    setFormErrors({});
    setFormError('');
    
    console.log('🔄 handleSubmit - Début de soumission');
    console.log('📋 Données du formulaire:', formData);
    console.log('✏️ Mode édition:', !!editingMember);
    
    try {
      let response;
      if (editingMember) {
        console.log('📝 Mise à jour du membre ID:', editingMember.id);
        console.log('📧 Email actuel:', editingMember.email);
        console.log('📧 Nouvel email:', formData.email);
        console.log('🔗 URL appelée:', `/members/${editingMember.id}`);
        response = await apiService.put(`/members/${editingMember.id}`, formData);
      } else {
        console.log('➕ Création d\'un nouveau membre');
        console.log('📧 Email à créer:', formData.email);
        response = await apiService.post('/members', formData);
      }
      
      console.log('✅ Réponse API:', response);
      
      setShowCreateModal(false);
      setEditingMember(null);
      resetForm();
      await loadMembers(pagination.current_page);
      await loadStats();
      
      console.log('🎉 Opération terminée avec succès');
    } catch (error) {
      console.error('❌ Erreur lors de l\'opération:', error);
      console.error('❌ Statut:', error.status);
      console.error('❌ Données:', error.data);
      console.error('❌ Erreurs de validation:', error.validationErrors);
      
      // Gestion des erreurs de validation (422)
      if (error.status === 422 && error.validationErrors) {
        setFormErrors(error.validationErrors);
        setFormError('Veuillez corriger les erreurs ci-dessous.');
      } else if (error.status === 422) {
        setFormError('Erreur de validation. Veuillez vérifier vos données.');
      } else {
        setFormError(`Erreur lors de l'opération: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Gestion de la pagination
//   const handlePageChange = (page) => {
//     if (page >= 1 && page <= (pagination?.last_page || 1)) {
//       setCurrentPage(page);
//       loadMembers(page);
//     }
//   };

  // Supprimer un membre
  const handleConfirmDelete = async () => {
    try {
      await apiService.delete(`/members/${selectedMember.id}`);
      setShowDeleteModal(false);
      setSelectedMember(null);
      loadMembers(pagination.current_page);
      loadStats();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert('Erreur lors de la suppression du membre');
    }
  };

  // Changer de page
  const handlePageChange = (page) => {
    console.log('🔄 Changement de page demandé:', { 
      from: pagination.current_page, 
      to: page,
      isValid: page >= 1 && page <= (pagination?.last_page || 1)
    });
    
    if (page >= 1 && page <= (pagination?.last_page || 1)) {
      // Mettre à jour l'état de pagination immédiatement
      setPagination(prev => ({ 
        ...prev, 
        current_page: page 
      }));
      
      // Charger les nouveaux membres
      console.log('📞 Appel loadMembers avec page:', page);
      loadMembers(page);
    } else {
      console.log('❌ Page invalide:', page, 'max:', pagination?.last_page);
    }
  };

  // Formatage des dates
  const formatDate = (dateString) => {
    if (!dateString) return 'Non spécifié';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  // Obtenir le badge de statut
  const getStatusBadge = (status) => {
    const statusConfig = {
      'actif': { color: 'bg-green-100 text-green-800', label: 'Actif' },
      'inactif': { color: 'bg-gray-100 text-gray-800', label: 'Inactif' },
      'suspendu': { color: 'bg-red-100 text-red-800', label: 'Suspendu' }
    };
    
    const config = statusConfig[status] || statusConfig['inactif'];
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  // Obtenir le badge de rôle
  const getRoleBadge = (role) => {
    const roleConfig = {
      'admin': { color: 'bg-purple-100 text-purple-800', label: 'Admin' },
      'apd': { color: 'bg-blue-100 text-blue-800', label: 'APD' },
      'apz': { color: 'bg-indigo-100 text-indigo-800', label: 'APZ' },
      'tgd': { color: 'bg-blue-100 text-blue-800', label: 'TGD' },
      'tgz': { color: 'bg-indigo-100 text-indigo-800', label: 'TGZ' },
      'mcd': { color: 'bg-teal-100 text-teal-800', label: 'MCD' },
      'mcz': { color: 'bg-cyan-100 text-cyan-800', label: 'MCZ' },
      'moderateur': { color: 'bg-orange-100 text-orange-800', label: 'Modérateur' },
      'membre': { color: 'bg-gray-100 text-gray-800', label: 'Membre' }
    };
    
    const config = roleConfig[role] || roleConfig['membre'];
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  // === FONCTIONS DE GESTION DES COTISATIONS ===

  // Ouvrir le modal d'ajout de cotisation pour un membre
  const handleOpenAddContribution = (member) => {
    setSelectedMemberForContribution(member);
    setContributionFormData({
      amount: member.contribution_monthly || '',
      period_month: new Date().getMonth() + 1,
      period_year: new Date().getFullYear(),
      status: 'pending',
      paid_at: '',
      notes: ''
    });
    setContributionError('');
    setShowAddContributionModal(true);
  };

  // Réinitialiser le formulaire de cotisation
  const resetContributionForm = () => {
    setSelectedMemberForContribution(null);
    setContributionFormData({
      amount: '',
      period_month: new Date().getMonth() + 1,
      period_year: new Date().getFullYear(),
      status: 'pending',
      paid_at: '',
      notes: ''
    });
    setContributionError('');
  };

  // Soumettre une cotisation
  const handleSubmitContribution = async (e) => {
    e.preventDefault();
    setContributionError('');
    
    if (!selectedMemberForContribution) {
      setContributionError('Veuillez sélectionner un membre.');
      return;
    }

    if (!contributionFormData.amount || contributionFormData.amount <= 0) {
      setContributionError('Le montant doit être supérieur à 0.');
      return;
    }

    try {
      setContributionLoading(true);
      
      // Préparer les données
      const payload = {
        user_id: selectedMemberForContribution.id,
        amount: parseFloat(contributionFormData.amount),
        period_month: parseInt(contributionFormData.period_month),
        period_year: parseInt(contributionFormData.period_year),
        status: contributionFormData.status,
        paid_at: contributionFormData.status === 'paid' && contributionFormData.paid_at 
          ? contributionFormData.paid_at 
          : null,
        notes: contributionFormData.notes || ''
      };

      console.log('📤 Envoi de la cotisation:', payload);

      // Appeler l'API pour créer la cotisation
      const response = await apiService.post('/contributions/payment', payload);
      
      console.log('✅ Cotisation créée:', response);
      
      // Fermer le modal et rafraîchir
      setShowAddContributionModal(false);
      resetContributionForm();
      
      // Actualiser les données
      await loadMembers(pagination.current_page);
      await loadStats();
      
    } catch (error) {
      console.error('❌ Erreur lors de l\'ajout de la cotisation:', error);
      setContributionError(
        error.message || 'Erreur lors de l\'ajout de la cotisation. Veuillez réessayer.'
      );
    } finally {
      setContributionLoading(false);
    }
  };

  // === FONCTIONS D'IMPORT/EXPORT ===

  // Exporter les membres vers Excel
  const handleExport = async () => {
    try {
      setExportLoading(true);
      const blob = await apiService.exportMembers();
      
      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `membres_fsf_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Erreur lors de l\'exportation:', error);
      alert('Erreur lors de l\'exportation des membres');
    } finally {
      setExportLoading(false);
    }
  };

  // Télécharger le template d'importation
  const handleDownloadTemplate = async () => {
    try {
      const blob = await apiService.downloadTemplate();
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'template_importation_membres.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Erreur lors du téléchargement du template:', error);
      alert('Erreur lors du téléchargement du template');
    }
  };

  // Gérer la sélection de fichier d'importation
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    setImportFile(file);
    setImportResult(null);
  };

  // Importer les membres depuis Excel
  const handleImport = async () => {
    if (!importFile) {
      alert('Veuillez sélectionner un fichier');
      return;
    }

    try {
      setImportLoading(true);
      const result = await apiService.importMembers(importFile);
      
      setImportResult(result.data);
      
      // Actualiser la liste des membres
      await loadMembers(pagination.current_page);
      await loadStats();
      
    } catch (error) {
      console.error('Erreur lors de l\'importation:', error);
      setImportResult({
        imported: 0,
        skipped: 0,
        errors: [error.message || 'Erreur lors de l\'importation'],
        total_processed: 0
      });
    } finally {
      setImportLoading(false);
    }
  };

  // Fermer le modal d'importation
  const handleCloseImportModal = () => {
    setShowImportModal(false);
    setImportFile(null);
    setImportResult(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-lg shadow-sm border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center py-4 space-y-3 sm:space-y-0">
            <div className="flex items-center space-x-2 sm:space-x-4 w-full sm:w-auto">
              <Link to="/" className="flex items-center text-gray-500 hover:text-fsf-primary transition-colors text-sm">
                <Home className="w-4 h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline font-medium">Retour au site</span>
                <span className="sm:hidden font-medium">Accueil</span>
              </Link>
              <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
              <Link to="/dashboard" className="text-gray-500 hover:text-fsf-primary transition-colors text-sm hidden sm:inline">
                Dashboard
              </Link>
              <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400 hidden sm:block" />
              <h1 className="text-lg sm:text-2xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                <span className="sm:hidden">Membres</span>
                <span className="hidden sm:inline">Gestion des Membres</span>
              </h1>
            </div>
            <div className="flex items-center justify-between w-full sm:w-auto space-x-2 sm:space-x-4">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 bg-white/70 border border-gray-200 rounded-xl hover:bg-white/90 transition-all duration-300 shadow-sm"
              >
                <RefreshCw className={`w-4 h-4 mr-1 sm:mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">Actualiser</span>
                <span className="sm:hidden">MAJ</span>
              </button>
              <div className="flex items-center space-x-2 sm:space-x-3">
                <span className="text-xs sm:text-sm font-medium text-gray-900 hidden sm:inline">
                  {user?.first_name || user?.name}
                </span>
                <div className="w-8 h-8 sm:hidden bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg flex items-center justify-center text-white font-bold text-sm">
                  {(user?.first_name || user?.name || 'A').charAt(0).toUpperCase()}
                </div>
                <span className="text-xs px-2 py-1 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-full">
                  {user?.role}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Statistiques rapides */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8">
          <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 p-4 sm:p-6">
            <div className="flex items-center">
              <div className="p-2 sm:p-3 bg-blue-100 rounded-xl">
                <Users className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
              </div>
              <div className="ml-3 sm:ml-4">
                <p className="text-xs sm:text-sm font-medium text-gray-500">Total Membres</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-900">{stats.total_members}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 p-4 sm:p-6">
            <div className="flex items-center">
              <div className="p-2 sm:p-3 bg-green-100 rounded-xl">
                <UserPlus className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
              </div>
              <div className="ml-3 sm:ml-4">
                <p className="text-xs sm:text-sm font-medium text-gray-500">Membres Actifs</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-900">{stats.active_members}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 p-4 sm:p-6">
            <div className="flex items-center">
              <div className="p-2 sm:p-3 bg-purple-100 rounded-xl">
                <Heart className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
              </div>
              <div className="ml-3 sm:ml-4">
                <p className="text-xs sm:text-sm font-medium text-gray-500">
                  <span className="sm:hidden">Nouveaux</span>
                  <span className="hidden sm:inline">Nouveaux ce mois</span>
                </p>
                <p className="text-lg sm:text-2xl font-bold text-gray-900">{stats.new_this_month}</p>
              </div>
            </div>
          </div>

          <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 p-4 sm:p-6">
            <div className="flex items-center">
              <div className="p-2 sm:p-3 bg-indigo-100 rounded-xl">
                <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600" />
              </div>
              <div className="ml-3 sm:ml-4">
                <p className="text-xs sm:text-sm font-medium text-gray-500">
                  <span className="sm:hidden">Croissance</span>
                  <span className="hidden sm:inline">Taux de croissance</span>
                </p>
                <p className="text-lg sm:text-2xl font-bold text-gray-900">{Math.round(stats.growth_rate || 0)}%</p>
              </div>
            </div>
          </div>
        </div>

        {/* Barre d'outils */}
        <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 p-4 sm:p-6 mb-6 sm:mb-8">
          <div className="space-y-4">
            {/* Première ligne : Recherche et boutons d'action */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
              {/* Recherche élargie */}
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                  <input
                    type="text"
                    placeholder="Rechercher un membre (nom, prénom, email, profession...)..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-9 sm:pl-11 pr-4 py-2.5 sm:py-3 text-sm sm:text-base border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white shadow-sm"
                  />
                </div>
              </div>

              {/* Boutons d'action */}
              <div className="flex flex-wrap gap-2 sm:gap-3">
                <button
                  onClick={handleExport}
                  disabled={exportLoading}
                  className="flex items-center px-3 sm:px-4 py-2.5 sm:py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl hover:from-green-600 hover:to-emerald-600 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 text-sm sm:text-base whitespace-nowrap"
                >
                  <Download className="w-4 h-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">{exportLoading ? 'Export...' : 'Exporter'}</span>
                  <span className="sm:hidden">{exportLoading ? 'Exp...' : 'Export'}</span>
                </button>

                {!isReadOnly && (
                <button
                  onClick={() => setShowImportModal(true)}
                  className="flex items-center px-3 sm:px-4 py-2.5 sm:py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-xl hover:from-blue-600 hover:to-indigo-600 transition-all duration-300 shadow-lg hover:shadow-xl text-sm sm:text-base whitespace-nowrap"
                >
                  <Upload className="w-4 h-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Importer</span>
                  <span className="sm:hidden">Import</span>
                </button>
                )}

                {!isReadOnly && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="flex items-center px-3 sm:px-4 py-2.5 sm:py-3 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-xl hover:from-purple-600 hover:to-blue-600 transition-all duration-300 shadow-lg hover:shadow-xl text-sm sm:text-base whitespace-nowrap"
                >
                  <Plus className="w-4 h-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Ajouter</span>
                  <span className="sm:hidden">Nouveau</span>
                </button>
                )}
              </div>
            </div>

            {/* Seconde ligne : Filtres */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 pt-3 sm:pt-2 border-t border-gray-100">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
              >
                <option value="">Tous les statuts</option>
                <option value="actif">Actifs</option>
                <option value="inactif">Inactifs</option>
                <option value="suspendu">Suspendus</option>
              </select>

              <select
                value={filterRole}
                onChange={(e) => setFilterRole(e.target.value)}
                className="px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
              >
                <option value="">Tous les rôles</option>
                <option value="admin">Admin</option>
                <option value="apd">APD</option>
                <option value="apz">APZ</option>
                <option value="moderateur">Modérateur</option>
                <option value="membre">Membre</option>
              </select>

              <select
                value={filterZone}
                onChange={(e) => setFilterZone(e.target.value)}
                className="px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
              >
                <option value="">Toutes les zones</option>
                {Array.isArray(organizationOptions?.zones) && organizationOptions.zones.length > 0 ? (
                  organizationOptions.zones.map((zone) => (
                    <option key={zone} value={zone}>📍 {zone}</option>
                  ))
                ) : (
                  <option disabled>Chargement...</option>
                )}
              </select>

              <select
                value={filterFraternity}
                onChange={(e) => {
                  console.log('🔍 Filtre fraternité changé:', e.target.value);
                  setFilterFraternity(e.target.value);
                }}
                className="px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
              >
                <option value="">Toutes les fraternités</option>
                {Array.isArray(organizationOptions?.fraternities) && organizationOptions.fraternities.length > 0 ? (
                  organizationOptions.fraternities.map((fraternity) => (
                    <option key={fraternity} value={fraternity}>⛪ {fraternity}</option>
                  ))
                ) : (
                  <option disabled>Chargement...</option>
                )}
              </select>
            </div>
          </div>
        </div>

        {/* Filtres et recherche */}
        {/* <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 p-6 mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher un membre..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-fsf-primary focus:border-transparent w-64"
                />
              </div>
              <div className="relative">
                <Filter className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="pl-10 pr-8 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-fsf-primary focus:border-transparent appearance-none bg-white"
                >
                  <option value="all">Tous les statuts</option>
                  <option value="active">Actifs</option>
                  <option value="inactive">Inactifs</option>
                </select>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">
                {filteredMembers.length} membre(s) trouvé(s)
              </span>
              <button 
                onClick={() => setShowCreateModal(true)}
                className="flex items-center px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl hover:from-green-600 hover:to-emerald-600 transition-all duration-300 shadow-lg"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Ajouter un membre
              </button>
            </div>
          </div>
        </div> */}

        {/* Liste des membres */}
        <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 overflow-hidden">
          {/* Pagination en haut - Debug forcé */}
          {!isLoading && filteredMembers.length > 0 && (
            <div className="px-6 py-4 border-b border-gray-200/50 bg-gray-50/30">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  {pagination ? (
                    <>Affichage de {pagination.from || 1} à {pagination.to || filteredMembers.length} sur {pagination.total || filteredMembers.length} résultats</>
                  ) : (
                    <>Affichage de {filteredMembers.length} résultats (pagination non définie)</>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  {/* Debug info */}
                  <div className="text-xs text-red-500 mr-4">
                    {/* Debug: total={pagination?.total}, per_page={pagination?.per_page}, last_page={pagination?.last_page}, current={pagination?.current_page} */}
                  </div>
                  
                  <button
                    onClick={() => {
                      console.log('🔄 Clic bouton précédent');
                      if (pagination?.current_page > 1) {
                        handlePageChange(pagination.current_page - 1);
                      }
                    }}
                    disabled={!pagination || pagination.current_page <= 1}
                    className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  
                  {/* Forcer l'affichage des boutons 1, 2, 3 pour le test */}
                  {[1, 2, 3].map(pageNum => (
                    <button
                      key={pageNum}
                      onClick={() => {
                        console.log(`🔄 Clic page ${pageNum}`);
                        handlePageChange(pageNum);
                      }}
                      className={`px-3 py-2 text-sm font-medium rounded-lg ${
                        pageNum === (pagination?.current_page || 1)
                          ? 'bg-indigo-600 text-white'
                          : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  ))}
                  
                  <button
                    onClick={() => {
                      console.log('🔄 Clic bouton suivant');
                      if (pagination?.current_page < 3) { // Force max 3 pages
                        handlePageChange((pagination?.current_page || 1) + 1);
                      }
                    }}
                    disabled={!pagination || (pagination?.current_page || 1) >= 3}
                    className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-fsf-primary"></div>
              <span className="ml-3 text-gray-600">Chargement des membres...</span>
            </div>
          ) : filteredMembers.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50/50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Membre
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                     <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                     Profession
                    </th>

                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rôle
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Statut
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date d'inscription
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white/50 divide-y divide-gray-200/50">
                  {filteredMembers.map((member) => (
                    <tr key={member.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-blue-500 rounded-xl flex items-center justify-center text-white font-bold">
                            {member.first_name?.charAt(0)?.toUpperCase() || 'U'}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-semibold text-gray-900">
                              {member.first_name} {member.last_name}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 space-y-1">
                          <div className="flex items-center">
                            <Mail className="w-4 h-4 text-gray-400 mr-2" />
                            {member.email}
                          </div>
                          {member.phone && (
                            <div className="flex items-center">
                              <Phone className="w-4 h-4 text-gray-400 mr-2" />
                              {member.phone}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {member.profession || 'Non spécifié'}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                          member.role === 'admin' 
                            ? 'bg-purple-100 text-purple-800'
                            : member.role === 'apd' || member.role === 'apz' || member.role === 'tgd' || member.role === 'tgz'
                            ? 'bg-blue-100 text-blue-800'
                            : member.role === 'mcd' || member.role === 'mcz'
                            ? 'bg-teal-100 text-teal-800'
                            : member.role === 'moderateur'
                            ? 'bg-indigo-100 text-indigo-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {member.role}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                          member.status === 'actif' 
                            ? 'bg-green-100 text-green-800'
                            : member.status === 'inactif'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {member.status === 'actif' ? 'Actif' : 
                           member.status === 'inactif' ? 'Inactif' : 'Suspendu'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(member.created_at).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        <button 
                          onClick={() => handleViewMemberDetails(member)}
                          className="text-indigo-600 hover:text-indigo-900 transition-colors p-1"
                          title="Voir les détails"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {!isReadOnly && (
                        <button 
                          onClick={() => handleOpenAddContribution(member)}
                          className="text-green-600 hover:text-green-900 transition-colors p-1"
                          title="Ajouter une cotisation"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                        )}
                        {!isReadOnly && (
                        <button 
                          onClick={() => handleEdit(member)}
                          className="text-gray-600 hover:text-gray-900 transition-colors p-1"
                          title="Modifier"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        )}
                        {user?.role === 'admin' && (
                          <button 
                            onClick={() => {
                              setMemberToDelete(member);
                              setShowDeleteModal(true);
                            }}
                            className="text-red-600 hover:text-red-900 transition-colors p-1"
                            title="Supprimer"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun membre trouvé</h3>
              <p className="text-gray-500 text-justify">
                {searchTerm ? 'Essayez de modifier vos critères de recherche.' : 'Commencez par ajouter des membres à votre communauté.'}
              </p>
            </div>
          )}

          {/* Pagination */}
          {pagination && pagination.total > pagination.per_page && (
            <div className="px-6 py-4 border-t border-gray-200/50 bg-white/30">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Affichage de {pagination.from} à {pagination.to} sur {pagination.total} résultats
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(pagination.current_page - 1)}
                    disabled={pagination.current_page === 1}
                    className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  
                  {Array.from({ length: Math.min(5, pagination.last_page) }, (_, i) => {
                    const pageNum = Math.max(1, Math.min(pagination.last_page - 4, pagination.current_page - 2)) + i;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-3 py-2 text-sm font-medium rounded-lg ${
                          pageNum === pagination.current_page
                            ? 'bg-indigo-600 text-white'
                            : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => handlePageChange(pagination.current_page + 1)}
                    disabled={pagination.current_page === pagination.last_page}
                    className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingMember ? 'Modifier le membre' : 'Ajouter un nouveau membre'}
                </h3>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setEditingMember(null);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Affichage des erreurs */}
              {formError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-red-800">{formError}</p>
                      {Object.keys(formErrors).length > 0 && (
                        <ul className="mt-2 list-disc list-inside text-sm text-red-700">
                          {Object.entries(formErrors).map(([field, errors]) => (
                            errors.map((error, index) => (
                              <li key={`${field}-${index}`}>
                                <strong>{formatFieldName(field)}:</strong> {error}
                              </li>
                            ))
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Informations de base */}
              <div className="space-y-4">
                <h4 className="text-sm font-semibold text-gray-800 border-b border-gray-200 pb-2">
                  Informations personnelles
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Prénom *
                    </label>
                    <input
                      type="text"
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent ${
                        formErrors.first_name 
                          ? 'border-red-300 focus:ring-red-500' 
                          : 'border-gray-300 focus:ring-purple-500'
                      }`}
                      required
                    />
                    {formErrors.first_name && (
                      <p className="mt-1 text-sm text-red-600">
                        {formErrors.first_name[0]}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nom *
                    </label>
                    <input
                      type="text"
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent ${
                        formErrors.last_name 
                          ? 'border-red-300 focus:ring-red-500' 
                          : 'border-gray-300 focus:ring-purple-500'
                      }`}
                      required
                    />
                    {formErrors.last_name && (
                      <p className="mt-1 text-sm text-red-600">
                        {formErrors.last_name[0]}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent ${
                        formErrors.email 
                          ? 'border-red-300 focus:ring-red-500' 
                          : 'border-gray-300 focus:ring-purple-500'
                      }`}
                      required
                    />
                    {formErrors.email && (
                      <p className="mt-1 text-sm text-red-600">
                        {formErrors.email[0]}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Téléphone
                    </label>
                    <input
                      type="tel"
                      value={formData.phone || ''}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="+229 12 34 56 78"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date de naissance
                    </label>
                    <input
                      type="date"
                      value={formData.date_of_birth || ''}
                      onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Genre
                    </label>
                    <select
                      value={formData.gender || ''}
                      onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="">Sélectionner...</option>
                      <option value="M">Masculin</option>
                      <option value="F">Féminin</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Adresse
                  </label>
                  <textarea
                    value={formData.address || ''}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Adresse complète..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Profession
                  </label>
                  <input
                    type="text"
                    value={formData.profession || ''}
                    onChange={(e) => setFormData({ ...formData, profession: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Profession ou activité..."
                  />
                </div>
              </div>

              {/* Informations organisationnelles */}
              <div className="space-y-4">
                <h4 className="text-sm font-semibold text-gray-800 border-b border-gray-200 pb-2 flex items-center">
                  🏢 Informations organisationnelles
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Zone
                    </label>
                    <select
                      value={formData.zone || ''}
                      onChange={(e) => setFormData({ ...formData, zone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="">Choisir une zone</option>
                      {organizationOptions.zones.map((zone) => (
                        <option key={zone} value={zone}>📍 {zone}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fraternité
                    </label>
                    <select
                      value={formData.fraternity || ''}
                      onChange={(e) => setFormData({ ...formData, fraternity: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="">Choisir une fraternité</option>
                      {organizationOptions.fraternities.map((fraternity) => (
                        <option key={fraternity} value={fraternity}>⛪ {fraternity}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Informations du couple */}
              <div className="space-y-4">
                <h4 className="text-sm font-semibold text-gray-800 border-b border-gray-200 pb-2 flex items-center">
                  💕 Informations du couple
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nom du couple
                    </label>
                    <input
                      type="text"
                      value={formData.couple_name || ''}
                      onChange={(e) => setFormData({ ...formData, couple_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Ex: Famille Dupont"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date de mariage
                    </label>
                    <input
                      type="date"
                      value={formData.marriage_date || ''}
                      onChange={(e) => setFormData({ ...formData, marriage_date: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Informations d'adhésion */}
              <div className="space-y-4">
                <h4 className="text-sm font-semibold text-gray-800 border-b border-gray-200 pb-2">
                  Informations d'adhésion
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date d'adhésion
                    </label>
                    <input
                      type="date"
                      value={formData.join_date || new Date().toISOString().split('T')[0]}
                      onChange={(e) => setFormData({ ...formData, join_date: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Contribution mensuelle (FCFA)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.contribution_monthly || 500}
                      onChange={(e) => setFormData({ ...formData, contribution_monthly: parseFloat(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Rôle
                    </label>
                    <select
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="membre">Membre</option>
                      <option value="moderateur">Modérateur</option>
                      <option value="sgd">SGD</option>
                      <option value="sgz">SGZ</option>
                      <option value="tgz">TGZ</option>
                      <option value="mcd">MCD</option>
                      <option value="mcz">MCZ</option>
                      {['admin', 'apd'].includes(user?.role) && (
                        <>
                          <option value="apd">APD</option>
                          <option value="tgd">TGD</option>
                          <option value="apz">APZ</option>
                          <option value="admin">Admin</option>
                        </>
                      )}
                      {!['admin', 'apd'].includes(user?.role) && ['apd', 'tgd', 'apz', 'admin'].includes(formData.role) && (
                        <option value={formData.role}>{formData.role.toUpperCase()}</option>
                      )}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Statut
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="actif">Actif</option>
                      <option value="inactif">Inactif</option>
                      <option value="suspendu">Suspendu</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes
                  </label>
                  <textarea
                    value={formData.notes || ''}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Notes ou commentaires supplémentaires..."
                  />
                </div>
              </div>

              {/* Authentification */}
              <div className="space-y-4">
                <h4 className="text-sm font-semibold text-gray-800 border-b border-gray-200 pb-2">
                  Authentification
                </h4>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mot de passe {!editingMember && '*'}
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    required={!editingMember}
                    placeholder={editingMember ? "Laisser vide pour ne pas changer" : "Minimum 8 caractères"}
                    minLength="8"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setEditingMember(null);
                    resetForm();
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg hover:from-purple-600 hover:to-blue-600 transition-all duration-300 disabled:opacity-50"
                >
                  {loading ? 'Enregistrement...' : (editingMember ? 'Modifier' : 'Enregistrement...')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de confirmation de suppression */}
      {showDeleteModal && memberToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Confirmer la suppression</h3>
                  <p className="text-sm text-gray-500">Cette action est irréversible.</p>
                </div>
              </div>
              
              <p className="text-sm text-gray-700 mb-6 text-justify">
                Êtes-vous sûr de vouloir supprimer le membre <strong>{memberToDelete.first_name} {memberToDelete.last_name}</strong> ?
              </p>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setMemberToDelete(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={() => handleDelete(memberToDelete.id)}
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Suppression...' : 'Supprimer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de détails du membre */}
      {showDetailsModal && selectedMemberDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold text-gray-900">👤 Détails du membre</h3>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Informations personnelles */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4">
                  <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    👤 Informations personnelles
                  </h4>
                  <div className="space-y-2 text-justify">
                    <p><span className="font-medium text-gray-700">Nom complet :</span> {selectedMemberDetails.first_name} {selectedMemberDetails.last_name}</p>
                    <p><span className="font-medium text-gray-700">Email :</span> 
                      <a href={`mailto:${selectedMemberDetails.email}`} className="text-blue-600 hover:text-blue-800 ml-1">
                        {selectedMemberDetails.email}
                      </a>
                    </p>
                    <p><span className="font-medium text-gray-700">Téléphone :</span> 
                      {selectedMemberDetails.phone ? (
                        <a href={`tel:${selectedMemberDetails.phone}`} className="text-blue-600 hover:text-blue-800 ml-1">
                          {selectedMemberDetails.phone}
                        </a>
                      ) : (
                        <span className="text-gray-500 ml-1">Non renseigné</span>
                      )}
                    </p>
                    <p><span className="font-medium text-gray-700">Date de naissance :</span> {selectedMemberDetails.date_of_birth || 'Non renseignée'}</p>
                    <p><span className="font-medium text-gray-700">Profession :</span> {selectedMemberDetails.profession || 'Non renseignée'}</p>
                    <p><span className="font-medium text-gray-700">Adresse :</span> {selectedMemberDetails.address || 'Non renseignée'}</p>
                    <p><span className="font-medium text-gray-700">Zone :</span> 
                      {selectedMemberDetails.zone ? (
                        <span className="ml-1 px-2 py-1 text-xs rounded-full bg-indigo-100 text-indigo-800">
                          📍 {selectedMemberDetails.zone}
                        </span>
                      ) : (
                        <span className="text-gray-500 ml-1">Non renseignée</span>
                      )}
                    </p>
                    <p><span className="font-medium text-gray-700">Fraternité :</span> 
                      {selectedMemberDetails.fraternity ? (
                        <span className="ml-1 px-2 py-1 text-xs rounded-full bg-teal-100 text-teal-800">
                          ⛪ {selectedMemberDetails.fraternity}
                        </span>
                      ) : (
                        <span className="text-gray-500 ml-1">Non renseignée</span>
                      )}
                    </p>
                    <p><span className="font-medium text-gray-700">Rôle :</span> 
                      <span className={`ml-1 px-2 py-1 text-xs rounded-full ${
                        selectedMemberDetails.role === 'admin' ? 'bg-red-100 text-red-800' :
                        selectedMemberDetails.role === 'moderateur' ? 'bg-orange-100 text-orange-800' :
                        selectedMemberDetails.role === 'membre' ? 'bg-blue-100 text-blue-800' :
                        selectedMemberDetails.role === 'apd' || selectedMemberDetails.role === 'tgd' ? 'bg-purple-100 text-purple-800' :
                        selectedMemberDetails.role === 'apz' || selectedMemberDetails.role === 'tgz' ? 'bg-green-100 text-green-800' :
                        selectedMemberDetails.role === 'mcd' || selectedMemberDetails.role === 'mcz' ? 'bg-teal-100 text-teal-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {selectedMemberDetails.role}
                      </span>
                    </p>
                    <p><span className="font-medium text-gray-700">Statut :</span> 
                      <span className={`ml-1 px-2 py-1 text-xs rounded-full ${
                        selectedMemberDetails.status === 'actif' ? 'bg-green-100 text-green-800' :
                        selectedMemberDetails.status === 'inactif' ? 'bg-gray-100 text-gray-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {selectedMemberDetails.status}
                      </span>
                    </p>
                  </div>
                </div>

                {/* Informations du couple */}
                <div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-lg p-4 text-justify">
                  <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    💕 Informations du couple
                  </h4>
                  <div className="space-y-2 text-justify">
                    <p><span className="font-medium text-gray-700">Nom du couple :</span> 
                      {selectedMemberDetails.couple_name || (
                        <span className="text-gray-500 ml-1">Non défini</span>
                      )}
                    </p>
                    <p><span className="font-medium text-gray-700">Date de mariage :</span> 
                      {selectedMemberDetails.marriage_date ? (
                        new Date(selectedMemberDetails.marriage_date).toLocaleDateString('fr-FR')
                      ) : (
                        <span className="text-gray-500 ml-1">Non renseignée</span>
                      )}
                    </p>
                    {coupleContributions && coupleContributions.length > 0 && (
                      <div className="mt-4 p-3 bg-white rounded-lg border">
                        <h5 className="font-medium text-gray-900 mb-2">📊 Statistiques du couple</h5>
                        <div className="space-y-1 text-sm">
                          <p><span className="font-medium">Cotisations payées :</span> {coupleContributions.filter(c => c.status === 'paid').length}/{coupleContributions.length}</p>
                          <p><span className="font-medium">Montant total payé :</span> {coupleContributions.filter(c => c.status === 'paid').reduce((sum, c) => sum + parseFloat(c.amount), 0).toLocaleString()} XOF</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Section contributions du couple */}
              {coupleContributions && coupleContributions.length > 0 && (
                <div className="mt-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    💳 Contributions du couple ({new Date().getFullYear()})
                  </h4>
                  <div className="bg-gray-50 rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="min-w-full">
                        <thead className="bg-gray-200">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Membre</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Période</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Montant</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date paiement</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {coupleContributions.map((contribution, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm text-gray-900">{contribution.user?.name || contribution.user_name}</td>
                              <td className="px-4 py-3 text-sm text-gray-900">{contribution.period_month}/{contribution.period_year}</td>
                              <td className="px-4 py-3 text-sm text-gray-900">{parseFloat(contribution.amount).toLocaleString()} XOF</td>
                              <td className="px-4 py-3 text-sm">
                                <span className={`px-2 py-1 text-xs rounded-full ${
                                  contribution.status === 'paid' ? 'bg-green-100 text-green-800' :
                                  contribution.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-red-100 text-red-800'
                                }`}>
                                  {contribution.status === 'paid' ? 'Payé' :
                                   contribution.status === 'pending' ? 'En attente' : 'Impayé'}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {contribution.payment_date ? new Date(contribution.payment_date).toLocaleDateString('fr-FR') : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-between mt-6">
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Fermer
                </button>
                <button
                  onClick={() => {
                    // Redirection vers la page des contributions du couple
                    const coupleId = selectedMemberDetails.couple_name ? 
                      encodeURIComponent(selectedMemberDetails.couple_name) : 
                      selectedMemberDetails.id;
                    navigate(`/cotisations/${coupleId}`);
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                >
                  📊 Voir toutes les contributions
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'ajout de cotisation */}
      {showAddContributionModal && selectedMemberForContribution && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  💳 Ajouter une cotisation
                </h3>
                <button
                  onClick={() => {
                    setShowAddContributionModal(false);
                    resetContributionForm();
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmitContribution} className="p-6 space-y-6">
              {/* Affichage des erreurs */}
              {contributionError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <AlertTriangle className="h-5 w-5 text-red-600" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-red-800">{contributionError}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Informations du membre */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-blue-900 mb-3 flex items-center">
                  👤 Membre sélectionné
                </h4>
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg flex items-center justify-center text-white font-bold">
                    {selectedMemberForContribution.first_name?.charAt(0)?.toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {selectedMemberForContribution.first_name} {selectedMemberForContribution.last_name}
                    </p>
                    <p className="text-xs text-gray-600">
                      {selectedMemberForContribution.email}
                    </p>
                    {selectedMemberForContribution.fraternity && (
                      <p className="text-xs text-gray-600 mt-1">
                        ⛪ {selectedMemberForContribution.fraternity}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Montant */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Montant de la cotisation (FCFA) *
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={contributionFormData.amount}
                  onChange={(e) => setContributionFormData({
                    ...contributionFormData,
                    amount: e.target.value
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Ex: 50000"
                  required
                />
              </div>

              {/* Période */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mois *
                  </label>
                  <select
                    value={contributionFormData.period_month}
                    onChange={(e) => setContributionFormData({
                      ...contributionFormData,
                      period_month: parseInt(e.target.value)
                    })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    {[
                      { value: 1, label: 'Janvier' },
                      { value: 2, label: 'Février' },
                      { value: 3, label: 'Mars' },
                      { value: 4, label: 'Avril' },
                      { value: 5, label: 'Mai' },
                      { value: 6, label: 'Juin' },
                      { value: 7, label: 'Juillet' },
                      { value: 8, label: 'Août' },
                      { value: 9, label: 'Septembre' },
                      { value: 10, label: 'Octobre' },
                      { value: 11, label: 'Novembre' },
                      { value: 12, label: 'Décembre' }
                    ].map(month => (
                      <option key={month.value} value={month.value}>
                        {month.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Année *
                  </label>
                  <select
                    value={contributionFormData.period_year}
                    onChange={(e) => setContributionFormData({
                      ...contributionFormData,
                      period_year: parseInt(e.target.value)
                    })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    {[2024, 2025, 2026, 2027].map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Statut */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Statut *
                </label>
                <select
                  value={contributionFormData.status}
                  onChange={(e) => setContributionFormData({
                    ...contributionFormData,
                    status: e.target.value
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="paid">✅ Payé</option>
                  <option value="pending">⏳ En attente</option>
                  <option value="overdue">⚠️ En retard</option>
                </select>
              </div>

              {/* Date de paiement (visible si statut = paid) */}
              {contributionFormData.status === 'paid' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date de paiement
                  </label>
                  <input
                    type="date"
                    value={contributionFormData.paid_at}
                    onChange={(e) => setContributionFormData({
                      ...contributionFormData,
                      paid_at: e.target.value
                    })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
              )}

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes
                </label>
                <textarea
                  value={contributionFormData.notes}
                  onChange={(e) => setContributionFormData({
                    ...contributionFormData,
                    notes: e.target.value
                  })}
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Commentaires supplémentaires (optionnel)..."
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddContributionModal(false);
                    resetContributionForm();
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={contributionLoading}
                  className="px-6 py-2 text-sm font-medium text-white bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg hover:from-green-600 hover:to-emerald-600 transition-all duration-300 disabled:opacity-50 flex items-center"
                >
                  {contributionLoading ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Enregistrement...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Ajouter la cotisation
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal d'importation */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <FileSpreadsheet className="w-5 h-5 mr-2 text-green-600" />
                  Importer des membres depuis Excel
                </h3>
                <button
                  onClick={handleCloseImportModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-justify">
                <h4 className="text-sm font-semibold text-blue-900 mb-2">📋 Instructions</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Téléchargez le template Excel pour voir le format requis</li>
                  <li>• Remplissez votre fichier selon le modèle</li>
                  <li>• Les champs "Prénom", "Nom" et "Email" sont obligatoires</li>
                  <li>• L'email doit être unique pour chaque membre</li>
                  <li>• Les dates doivent être au format JJ/MM/AAAA</li>
                </ul>
              </div>

              {/* Informations sur les zones et fraternités */}
              {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-indigo-900 mb-2 flex items-center">
                    📍 Zones Disponibles
                  </h4>
                  <div className="text-sm text-indigo-800 space-y-1">
                    {organizationOptions.zones.length > 0 ? (
                      organizationOptions.zones.map((zone, index) => (
                        <div key={index} className="flex items-center">
                          <span className="w-2 h-2 bg-indigo-400 rounded-full mr-2"></span>
                          {zone}
                        </div>
                      ))
                    ) : (
                      <p className="text-xs italic">Aucune zone définie</p>
                    )}
                  </div>
                </div>

                <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-teal-900 mb-2 flex items-center">
                    ⛪ Fraternités Disponibles
                  </h4>
                  <div className="text-sm text-teal-800 space-y-1">
                    {organizationOptions.fraternities.length > 0 ? (
                      organizationOptions.fraternities.map((fraternity, index) => (
                        <div key={index} className="flex items-center">
                          <span className="w-2 h-2 bg-teal-400 rounded-full mr-2"></span>
                          {fraternity}
                        </div>
                      ))
                    ) : (
                      <p className="text-xs italic">Aucune fraternité définie</p>
                    )}
                  </div>
                </div>
              </div> */}

              {/* Bouton de téléchargement du template */}
              {/* <div className="text-center space-y-3">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-green-900 mb-2 flex items-center justify-center">
                    📋 Template Excel Mis à Jour
                  </h4>
                  <div className="text-sm text-green-800 space-y-1">
                    <p>✅ <strong>Nouveaux champs ajoutés :</strong> Zone et Fraternité</p>
                    <p>📑 <strong>Feuille d'instructions incluse</strong> avec toutes les valeurs acceptées</p>
                    <p>📝 <strong>Exemples de données</strong> pour faciliter la saisie</p>
                  </div>
                </div>
                
                <button
                  onClick={handleDownloadTemplate}
                  className="flex items-center justify-center px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-lg hover:from-blue-600 hover:to-indigo-600 transition-all duration-300 shadow-lg mx-auto"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Télécharger le template Excel
                </button>
                
                <p className="text-xs text-gray-600 mt-2">
                  💡 <em>Le template contient maintenant tous les champs Zone et Fraternité listés ci-dessus</em>
                </p>
              </div> */}

              {/* Sélection de fichier */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sélectionner un fichier Excel (.xlsx, .xls, .csv)
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                  <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <div className="space-y-2">
                    <input
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      onChange={handleFileSelect}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"
                    />
                  </div>
                  {importFile && (
                    <div className="mt-2 text-sm text-green-600 font-medium">
                      ✅ {importFile.name}
                    </div>
                  )}
                </div>
              </div>

              {/* Résultats de l'importation */}
              {importResult && (
                <div className={`rounded-lg p-4 ${
                  importResult.errors && importResult.errors.length > 0 
                    ? 'bg-yellow-50 border border-yellow-200' 
                    : 'bg-green-50 border border-green-200'
                }`}>
                  <h4 className="text-sm font-semibold mb-2 text-justify">📊 Résultats de l'importation</h4>
                  <div className="text-sm space-y-1 text-justify">
                    <p><strong>Total traité :</strong> {importResult.total_processed || 0}</p>
                    <p><strong>Importés avec succès :</strong> <span className="text-green-600">{importResult.imported || 0}</span></p>
                    <p><strong>Ignorés :</strong> <span className="text-yellow-600">{importResult.skipped || 0}</span></p>
                    
                    {importResult.errors && importResult.errors.length > 0 && (
                      <div className="mt-3">
                        <p className="font-semibold text-red-600 mb-1">Erreurs :</p>
                        <div className="max-h-32 overflow-y-auto bg-white rounded border p-2">
                          {importResult.errors.map((error, index) => (
                            <p key={index} className="text-xs text-red-600 mb-1">• {error}</p>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  onClick={handleCloseImportModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Fermer
                </button>
                <button
                  onClick={handleImport}
                  disabled={!importFile || importLoading}
                  className="px-6 py-2 text-sm font-medium text-white bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg hover:from-green-600 hover:to-emerald-600 transition-all duration-300 disabled:opacity-50 flex items-center"
                >
                  {importLoading ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Importation...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Importer
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MembersPage;